import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import { applyFetchPreconditions } from '@de/utils/decisions/applyFetchPreconditions';
import { collectFactBindings } from '@de/utils/decisions/collectFactBindings';
import { toDecisionsChannel } from '@de/utils/decisions/decisionsChannel';
import {
  DECISIONS_CONTEXT_KEY,
  PLATFORM_VARIANT_CONTEXT_KEY,
} from '@de/utils/decisions/embeddedDecisions';
import { findHostOnlyStepHandles } from '@de/utils/configurable-step/findHostOnlyStepHandles';
import { warnOnce } from '@de/utils/warnOnce';
import { HostSeededFormKeysProvider } from '@de/providers/HostSeededFormKeys';
import { useEmitObservedFormStateChange } from '@de/providers/ObservedFormState';
import {
  DataSourceManagerProvider,
  useDataSourceManager,
} from '@de/providers/DataSourceManager';
import { useTaxonomy } from '@de/providers/TaxonomyProvider';
import {
  useApiConfig,
  useSiteConfig,
} from '@de/providers/ExperienceConfigProvider';
import { mobiusConfig } from '@de/constants/mobius';
import type {
  FormState,
  FormStateValue,
  ResolvedDataSource,
} from '@de/providers/DataSourceManager.types';
import { ActionDispatcherContext } from '@de/utils/action-resolver/ActionDispatcher';
import {
  attachServiceCredentials,
  buildBoundComponentIndex,
  buildBoundFieldIndex,
  buildFlowContext,
  projectRequestContext,
  buildLeadContext,
  buildLinkoutTrackingContext,
  buildTrackingContext,
  buildMobiusBodyCoercer,
  buildOfferingsMatchResponseCoercer,
  buildOfferingsPagingBodyCoercer,
  buildOfferingsTaxonomyBodyCoercer,
  buildRankingsResponseCoercer,
  composeRequestBodyCoercers,
  composeResponseCoercers,
  createActionDispatcher,
  dispatchNamedModalAction,
  deriveInitialFormState,
  resolveLandingHostFormSeed,
  deriveTrustedOriginsFromApiConfig,
  deriveReportingQueryUrl,
  appendReportingQueryOrigin,
  extractDataSources,
  extractResultsPageConfig,
  findBoundComponent,
  getEntryPointConfigFromSteps,
  isResultLike,
  isSubmittableResult,
  readInferredCountryCode,
  readStepFlowType,
  readStepMonetizationChannel,
  resolveDataSourceDeclarations,
  resolveGeolocation,
  translateFormStateChange,
  type BoundComponentInfo,
} from '@de/utils/configurable-step';
import { resolveTrackingTenantId } from '@de/utils/buildConversionCreationRequest';
import { emitLeadFormSubmitted } from '@de/utils/configurable-step/emitLeadFormSubmitted';
import { resolveTrafficSource } from '@de/utils/resolveTrafficSource';
import { fetchDataSource } from '@de/utils/data-sources/fetchDataSource';
import { resolveOrganizationIds } from '@de/utils/data-fetch/resolveOrganizationIds';
import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import type {
  ConfigurableStepProps,
  ResultsPageConfig,
  ResultsPageExitStrategyExtraCtaConfig,
  ResultsPageExitStrategyStudentLoansInlineConfig,
  StudentLoansInlineDslContent,
} from '@de/types/types';
import { useTracking } from '@de/providers/TrackingProvider';
import {
  ComparisonProvider,
  useComparisonOptional,
} from '@de/providers/ComparisonProvider';
import {
  ResultsPageConfigProvider,
  useResultsPageConfig,
} from '@de/providers/ResultsPageConfigProvider';
import {
  LeadFlowProvider,
  useLeadFlowOptional,
} from '@de/providers/LeadFlowProvider';
import {
  ModalHostProvider,
  useModalHostOptional,
} from '@highereducation/hen-ui';
import {
  SubmittedProgramsProvider,
  useSubmittedProgramsOptional,
} from '@de/providers/SubmittedProgramsProvider';
import {
  emitCompareAdded,
  emitCompareRemoved,
} from '@de/components/DegreeCardDetailed/compareAnalytics';
import DslConfig from './DslConfig';
import DslResultsPagePopups from './DslResultsPagePopups';
import DslSubmissions from './DslSubmissions';
import ComparisonRuntime from '../../ComparisonModal/ComparisonRuntime';
import Loading from '../../Loading';

export type { ConfigurableStepProps } from '@de/types/types';

const RESULT_PAGE_EXCLUDED_CONTENT_TYPES = ['StudentLoansInline'] as const;

/**
 * Result-page loading stabilization is intentionally keyed to the canonical
 * primary results source name. DSL configs using another source name still
 * render gracefully, but they opt out of the interstitial unless authoring
 * validation maps their primary source to this convention.
 */
const PRIMARY_RESULTS_SOURCE_NAME = 'results';

/**
 * `ConfigurableStep` -- the convergence point between the DE flow engine
 * (`DynamicExperienceProvider` / `flowState` / `flowActions`) and the
 * data-binding stack (`DataSourceManagerProvider` + `_bind` enhancer +
 * Puck `<Render>`). Per child SAD ADR-15.
 *
 * Responsibilities (S8 minimum):
 *   1. Read DE context via {@link useDynamicExperienceContext}.
 *   2. Build `flowContext` from the prior steps' `Option[]` selections
 *      and pass it to the `DataSourceManagerProvider` so DSL templates
 *      can resolve `{{context.<stepId>}}` /
 *      `{{context.<stepId>_label}}` / `{{context.<leadField>}}`.
 *   3. Translate `onFormStateChange(nextFormState)` into DE
 *      `updateFlowState(stepId, { option | options[], leadFieldAssociated })`
 *      writes by walking the DSL `data.content` tree to find the bound
 *      component and resolving the display label from the DSM source.
 *   4. Install a minimal `ActionDispatcher` whose only registered type
 *      is `navigation.advanceStep` (plus optional `goToStep` / `goBack`
 *      when the host context exposes them). DB-2.1 will replace this
 *      with the full registry.
 *   5. Render `<DslConfig>` inside the provider stack so
 *      `applyBindings` (DB-1.4) can resolve every `_bind.*` /
 *      `_action.*` / `formKey` / `_meta.*` prop at render time.
 *
 * Provider order today (innermost-out):
 *
 *   `<DataSourceManagerProvider>`
 *     `<ActionDispatcherContext.Provider>`
 *       (S9 wrapper holes for Tracking / Eventing / Comparison)
 *         `<DslConfig>`
 *
 * Per child SAD ADR-14 the canonical full stack is
 * `Tracking → Eventing → DSM → Comparison → ActionDispatcher → Puck Render`.
 * S9 will wrap additional providers around / inside this component
 * without modifying it.
 *
 * Mount the bridge with
 * `key={dslConfigId ?? currentStep.id ?? 'configurable'}` so each new
 * DSL config remounts the DSM with a fresh cache (POC bug fix per plan
 * §3.8).
 */
const ConfigurableStep: React.FC<ConfigurableStepProps> = ({
  dslConfig,
  flowType: parentStepFlowType,
  trafficSource: parentStepTrafficSource,
  renderErrorFallback,
  onRenderError,
  leadSubmitDryRun,
}) => {
  const { currentStep } = useDynamicExperienceContext();
  const isResultDslConfig = dslConfig?.type === 'result';
  const resultsPageConfig = useMemo(
    () =>
      isResultDslConfig ? extractResultsPageConfig(dslConfig?.data) : undefined,
    [dslConfig?.data, isResultDslConfig],
  );
  const comparisonConfig = resultsPageConfig?.comparison;
  const flowType =
    readFlowTypeValue(parentStepFlowType) ??
    readStepFlowType(currentStep) ??
    readResultsPageFlowType(resultsPageConfig);
  const trafficSource =
    readFlowTypeValue(parentStepTrafficSource) ??
    resolveTrafficSource(currentStep);
  const monetizationChannel = readStepMonetizationChannel(currentStep);
  const comparisonEnabled =
    isResultDslConfig && comparisonConfig?.enabled === true;
  const studentLoansInline =
    resultsPageConfig?.exitStrategy?.studentLoansInline;
  const studentLoansInlineContent =
    isResultDslConfig && studentLoansInline?.enabled === true
      ? normalizeStudentLoansInlineContent(studentLoansInline)
      : undefined;
  const extraCta = resultsPageConfig?.exitStrategy?.extraCta;
  const extraCtaConfig: ResultsPageExitStrategyExtraCtaConfig | undefined =
    isResultDslConfig && extraCta?.enabled === true
      ? {
          enabled: true,
          ...(extraCta.label !== undefined ? { label: extraCta.label } : {}),
          ...(extraCta.eventName !== undefined
            ? { eventName: extraCta.eventName }
            : {}),
        }
      : undefined;

  const bridge = (
    <ConfigurableStepBridge
      dslConfig={dslConfig}
      comparisonEnabled={comparisonEnabled}
      flowType={flowType}
      trafficSource={trafficSource}
      monetizationChannel={monetizationChannel}
      renderErrorFallback={renderErrorFallback}
      onRenderError={onRenderError}
      leadSubmitDryRun={leadSubmitDryRun}
    />
  );

  const withComparison = comparisonEnabled ? (
    <ComparisonProvider
      minPrograms={comparisonConfig?.minSelection}
      maxPrograms={comparisonConfig?.maxSelection}
      dslConfigId={dslConfig?.configId}
    >
      {bridge}
      <ComparisonRuntime />
    </ComparisonProvider>
  ) : (
    bridge
  );

  const withModalHost =
    typeof ModalHostProvider === 'function' ? (
      <ModalHostProvider>{withComparison}</ModalHostProvider>
    ) : (
      withComparison
    );

  if (!isResultDslConfig) {
    return withModalHost;
  }

  return (
    <ResultsPageConfigProvider config={resultsPageConfig}>
      <SubmittedProgramsProvider>
        <LeadFlowProvider
          dslConfigId={dslConfig?.configId}
          flowType={flowType}
          trafficSource={trafficSource}
          monetizationChannel={monetizationChannel}
          studentLoansInlineContent={studentLoansInlineContent}
          extraCtaConfig={extraCtaConfig}
        >
          {withModalHost}
        </LeadFlowProvider>
      </SubmittedProgramsProvider>
    </ResultsPageConfigProvider>
  );
};

function normalizeStudentLoansInlineContent(
  config: ResultsPageExitStrategyStudentLoansInlineConfig,
): StudentLoansInlineDslContent {
  const { enabled: _enabled, ...props } = config;
  return {
    type: 'StudentLoansInline',
    props: {
      ...props,
      variant: 'narrow',
    },
  };
}

function readResultsPageFlowType(
  config: ResultsPageConfig | undefined,
): string | undefined {
  return readFlowTypeValue(config?.flowType);
}

function readFlowTypeValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

interface ConfigurableStepBridgeProps extends ConfigurableStepProps {
  comparisonEnabled: boolean;
  flowType?: string;
  trafficSource?: string;
  monetizationChannel?: string;
}

const ConfigurableStepBridge: React.FC<ConfigurableStepBridgeProps> = ({
  dslConfig,
  comparisonEnabled,
  flowType,
  trafficSource,
  monetizationChannel,
  renderErrorFallback,
  onRenderError,
  leadSubmitDryRun,
}) => {
  const ctx = useDynamicExperienceContext();
  const taxonomy = useTaxonomy();
  const taxonomyRef = useRef(taxonomy);
  taxonomyRef.current = taxonomy;
  const apiConfig = useApiConfig();
  const siteConfig = useSiteConfig();
  const pageTenantId = siteConfig?.tenantId?.trim();
  const isResultDslConfig = dslConfig?.type === 'result';
  const dryRun = leadSubmitDryRun === true;

  /**
   * The **step-level** event sequence (ElementLoaded /
   * ProductLoaded / StepElementLoaded on mount, FormViewed /
   * ProductViewed on viewport intersect) is owned by the standard
   * render path in `Experience.tsx`. `Experience` already mounts the
   * sequence around its `<ComponentRenderer>` (which routes
   * `dslConfigId`-bearing steps here), so duplicating the sequence
   * inside this bridge would emit every event twice in production.
   *
   * The bridge's analytics responsibility is therefore narrow:
   *   1. Provide the `setEntryRootNode` ref via {@link EntryRoot} so
   *      the inherited `TrackingProvider`'s IO observer can see the
   *      DSL subtree.
   *   2. Let individual DSL nodes (wrapped in {@link Trackable} by the
   *      binding-enhancer) fire **their own** ElementViewed /
   *      FieldViewed / FieldInputted / ProductClicked through the
   *      inherited provider — sub-step events with no analogue on
   *      the standard render path.
   *
   * Correlation IDs are inherited from the surrounding
   * `DynamicExperienceProvider` — the DSL render path does not
   * generate its own.
   */
  // The DSL author writes path-only endpoints (`/v1/search`,
  // `/v3/schema/...`) per the data-source allowlist; the bridge owns
  // the runtime base-URL contract by pulling service base URLs from
  // `useApiConfig()` and resolving each path into a full URL via
  // `resolveDataSourceUrl()`. Absolute-URL declarations (legacy or
  // tenant-specific) flow through unchanged and are gated by the
  // trusted-origin set derived alongside. Data-source declarations
  // need an explicit runtime service / base-URL contract at the
  // bridge boundary; the bridge is that explicit contract.
  // A source only conditional variant bindings read is fetched only while
  // one of their preconditions holds, the same as on a standalone DSL page.
  const rawDataSources = useMemo(() => {
    const declared = extractDataSources(dslConfig?.data);
    return (
      applyFetchPreconditions(
        declared,
        collectFactBindings(dslConfig?.data),
        dslConfig?.data,
      ) ?? declared
    );
  }, [dslConfig?.data]);
  const resolvedDataSources = useMemo(
    () =>
      resolveDataSourceDeclarations(rawDataSources, apiConfig, pageTenantId),
    [rawDataSources, apiConfig, pageTenantId],
  );

  /**
   * Auto-attach host-supplied service credentials (today: the
   * Mobius publisher idToken) to outbound declarations whose origin
   * matches a configured service. Authors never write
   * `{{context.identityToken}}` by hand -- the bridge owns the
   * credential contract end-to-end so a misnamed template can't
   * silently leave a request unauthenticated. See
   * `attachServiceCredentials` for the per-service policy table.
   */
  /**
   * Pulls the Mobius publisher idToken from the same constants module
   * the legacy `MobiusApiClient.getLocationInference()` reads
   * (`@de/constants/mobius`). Keeping the credential source identical
   * between the legacy client and the DSM-driven bridge is intentional:
   * Mobius authenticates the request with this exact token, and
   * threading it through the host's `siteConfig.identityToken`
   * (which today is `undefined` for the dynamic-flows app) would
   * silently 403 every Mobius source the moment the bridge moved
   * past path-only diagnostics.
   *
   * If/when the host promotes `identityToken` onto `siteConfig`, the
   * fallback chain here can prefer it (host-provided > package-level
   * default) without changing `attachServiceCredentials`.
   */
  const dataSources = useMemo(
    () =>
      attachServiceCredentials(resolvedDataSources, apiConfig, {
        identityToken: mobiusConfig.identityToken,
      }),
    [resolvedDataSources, apiConfig],
  );
  const reportingQueryUrl = useMemo(
    () =>
      deriveReportingQueryUrl(apiConfig, {
        sameOriginBase:
          typeof window !== 'undefined' ? window.location.origin : undefined,
      }),
    [apiConfig],
  );
  const trustedOrigins = useMemo(
    () =>
      appendReportingQueryOrigin(
        deriveTrustedOriginsFromApiConfig(apiConfig),
        reportingQueryUrl,
      ),
    [apiConfig, reportingQueryUrl],
  );
  /**
   * Post-interpolation request-body transforms, composed into the single
   * `coerceRequestBody` hook DSM applies before the network call. Built
   * here -- next to the trustedOrigins + service-credential wiring that
   * already encode Mobius/Offerings bridge knowledge -- to keep DSM
   * service-agnostic. Memoized on `apiConfig` so a stable origin doesn't
   * churn the transform reference.
   *   - Mobius numeric-id coercion: Mobius rejects string-typed integer
   *     IDs (`JSONArrayableInt64`); DSM's `formState` is
   *     `Record<string, string>`.
   *   - Offerings taxonomy sanitize: repairs unselected DCS dimensions
   *     (`[undefined]` → `["general"]`) so a fully-unselected triad
   *     shows general programs instead of shipping a `[null]` the
   *     search DTO rejects.
   *   - Offerings search paging: `offset` and `limit` are integers.
   *     Form state stores them as strings, and the search DTO rejects
   *     that. The rule is the search path only.
   */
  const coerceRequestBody = useMemo(
    () =>
      composeRequestBodyCoercers(
        buildMobiusBodyCoercer(apiConfig),
        buildOfferingsTaxonomyBodyCoercer(apiConfig),
        buildOfferingsPagingBodyCoercer(apiConfig),
      ),
    [apiConfig],
  );
  /**
   * Service-shaped response normalizers, run on the raw JSON before
   * `responsePath` extraction. Each is individually gated on request
   * method/path/origin and is a no-op otherwise, so composing them is safe:
   *   - Offerings `/match`: reshapes UUID-native bucket items into the flat
   *     `Result` card contract, so cards/lead-flow/dedupe/by-id drawer all
   *     consume one stable shape.
   *   - Rankings `/v1/rankings/{listKey}`: pre-derives the composite
   *     `RankingCard` arrays (`marketing.stats` / `.details` / `.badges`)
   *     from the flat keyed marketing payload.
   * Interim seams; durable fix is service-/BFF-side normalization. Built here
   * next to the other Offerings/Mobius bridge knowledge to keep DSM
   * service-agnostic; memoized on `apiConfig` so a stable origin doesn't churn
   * the ref.
   */
  const coerceResponse = useMemo(
    () =>
      composeResponseCoercers(
        buildOfferingsMatchResponseCoercer(apiConfig),
        buildRankingsResponseCoercer(apiConfig),
      ),
    [apiConfig],
  );

  const rawComparison = useComparisonOptional();
  const comparison = comparisonEnabled ? rawComparison : undefined;

  // Submitted-programs registry. Present only when an
  // ancestor mounted a `SubmittedProgramsProvider` (result configs do, via
  // the outer `ConfigurableStep`). Its snapshot is published into
  // `flowContext` below so DSL cards can bind submitted state read-only.
  const submitted = useSubmittedProgramsOptional();

  // Resolve once per steps change — `extractEntryPointConfig` can
  // `console.warn` on malformed blocks, so keep it off the hot
  // `buildFlowContext` path (which re-runs on every formState update).
  const subjectLabelFallback = useMemo(
    () => getEntryPointConfigFromSteps(ctx.steps ?? [])?.subjectLabelFallback,
    [ctx.steps],
  );

  // `buildFlowContext` never writes a host-only key; tell the author which
  // step handle was refused, once per steps change.
  useEffect(() => {
    for (const { stepId, field, handle } of findHostOnlyStepHandles(
      ctx.steps,
    )) {
      warnOnce(
        'host-only-context',
        `[ConfigurableStep] Refused \`${field}: "${handle}"\` on step "${stepId}": context.${handle} is filled only by the page host.`,
      );
    }
  }, [ctx.steps]);

  // Built once per host update, so a post-load update that changes nothing
  // else keeps every other context entry, and each binding's inputs, stable.
  const decisionsChannel = useMemo(
    () =>
      ctx.decisions === undefined
        ? undefined
        : toDecisionsChannel(ctx.decisions),
    [ctx.decisions],
  );
  const platformVariant =
    typeof ctx.platformVariant === 'string' && ctx.platformVariant.trim()
      ? ctx.platformVariant.trim()
      : undefined;

  /**
   * Reserved `flowContext` keys exposed for outbound URL building.
   * `attachServiceCredentials` already auto-injects `idToken` for
   * **inbound** Mobius requests, but the binding-resolver
   * deliberately does NOT see credentials on that path (so a typo in
   * a DSL template can't silently leave a request unauthenticated).
   *
   * Outbound link building (e.g. `<a href={…}>` to Mobius's
   * `/v1/redirect/{programId}/{capId}` endpoint, which mandates
   * `?idToken=…&helid=…` to actually redirect) has the inverse
   * failure mode: a missing token produces a no-op click that's
   * caught the moment a user tries it. Surfacing the same token to
   * `{{context.mobiusIdToken}}` lets DSL authors compose the
   * outbound URL via interpolation
   * (`{{item.product.destination.url}}?idToken={{context.mobiusIdToken}}&helid={{item.product.destination.helid}}`)
   * without needing a bespoke transform or component.
   *
   * Spread last so a user-defined step id with the same name cannot
   * silently shadow the credential. Keys here should stay narrow and
   * use a service-prefixed shape (`mobiusIdToken`, future
   * `audienceIntentToken`) so they don't collide with author-chosen
   * step ids in practice.
   */
  const flowContext = useMemo(() => {
    // Assemble the LDS lead from the answered flow questions (legacy
    // `transformFlowStateToLeadInputs` → `buildLeadObject` shape). Exposed
    // as `context.lead` so a result-page config can drive the Offerings
    // `/v1/offerings/search/match` qualification hop via
    // `body: { lead: "{{context.lead}}" }`. Spread conditionally so the key
    // is absent (not `undefined`) when assembly fails / the flow is empty,
    // which degrades cleanly to the no-lead `/match` path.
    const lead = buildLeadContext(ctx.flowState);
    // Cohesion-derived tracking context (anonymousId / writeKey /
    // correlationId), exposed as `context.trackingContext` so an offerings
    // data source can forward it via `body: { trackingContext:
    // "{{context.trackingContext}}" }`. Spread conditionally so the key is
    // absent (not `undefined`) when nothing is resolvable, degrading cleanly
    // to the offerings service's own tracking fallbacks.
    const trackingContext = buildTrackingContext(ctx.correlationId);
    // Curated tenant/publisher metadata, namespaced under the single reserved
    // `tenant` key. Authors read `{{context.tenant.id}}` /
    // `{{context.tenant.name}}` / `{{context.tenant.domain}}` so
    // publisher-specific copy (e.g. the rankings methodology heading) is not
    // hardcoded. This is an explicit allowlist — never a passthrough of the
    // full publisher config — so ad/monetization/feature internals can't leak
    // into interpolated copy. The flat `context.tenantId` (below) is preserved
    // for back-compat; `tenant.id` mirrors it.
    //
    // `siteConfig.displayName` is host-populated from the tenants API
    // `tenant.name` at two upstream sources in `apps/hen-site`:
    // `buildInitialGlobalProps` (WordPress shortcode mount) and
    // `buildConfigProps` (/discover/hen mount). If `{{context.tenant.name}}`
    // ever resolves empty, check those two builders first.
    const tenantName = siteConfig?.displayName?.trim();
    const tenantDomain = siteConfig?.domain?.trim();
    const tenant =
      pageTenantId || tenantName || tenantDomain
        ? {
            ...(pageTenantId ? { id: pageTenantId } : {}),
            ...(tenantName ? { name: tenantName } : {}),
            ...(tenantDomain ? { domain: tenantDomain } : {}),
          }
        : undefined;
    return {
      ...buildFlowContext(ctx.flowState, ctx.steps, ctx.currentStep, {
        subjectLabelFallback,
      }),
      ...(flowType ? { flowType } : {}),
      ...(trafficSource ? { trafficSource } : {}),
      mobiusIdToken: mobiusConfig.identityToken,
      // Same slug `navigation.linkout` uses — lets portable result configs
      // scope offerings `/match` budget via `body.tenantId: "{{context.tenantId}}"`.
      ...(pageTenantId ? { tenantId: pageTenantId } : {}),
      // Page-authored headline from the host (affiliate adapter extracts it
      // from the legacy tag's light DOM). Entry configs interpolate
      // `{{context.headline.text}}` (or the `prefix`/`emphasis`/`suffix`
      // segments for two-tone headings) to adopt the page's copy.
      ...(ctx.headline ? { headline: ctx.headline } : {}),
      // Generic per-instance params from the host, namespaced under the
      // single reserved `params` key so a reusable DSL resolves
      // placement-scoped values via `{{context.params.*}}`. Nesting under one
      // key keeps individual params from colliding with the step-derived
      // top-level `context.*` keys from `buildFlowContext`; spread after it
      // (like `mobiusIdToken` / `tenantId`) so host params win on the sole
      // possible collision — a step whose `leadFieldAssociated`/id is literally
      // `params`.
      ...(ctx.params ? { params: ctx.params } : {}),
      // Allowlisted visitor facts. Always present, including when the
      // host passed nothing, so a missing fact reads as "unknown"
      // rather than an empty string. Spread after `buildFlowContext`
      // so an author handle named `request` cannot replace it.
      request: projectRequestContext(ctx.requestContext),
      // Reserved, collision-safe like `params`/`headline`; spread after
      // `buildFlowContext` so a literal `tenant` author handle can't clobber it.
      ...(tenant ? { tenant } : {}),
      ...(lead ? { lead } : {}),
      ...(trackingContext ? { trackingContext } : {}),
      ...(comparison ? { comparison: comparison.snapshot } : {}),
      // Read-only submitted-state exposure. A card binds aggregate fields
      // e.g. `_bind.<prop>: "{{context.submissions.count}}"`; per-program
      // submitted state reaches cards via the `useSubmittedForProgram` hook.
      ...(submitted ? { submissions: submitted.snapshot } : {}),
      // Host-only rule results, spread last. `buildFlowContext` never emits
      // these keys, so only the page host fills them.
      ...(decisionsChannel
        ? { [DECISIONS_CONTEXT_KEY]: decisionsChannel }
        : {}),
      ...(platformVariant
        ? { [PLATFORM_VARIANT_CONTEXT_KEY]: platformVariant }
        : {}),
    };
  }, [
    ctx.flowState,
    ctx.steps,
    ctx.currentStep,
    ctx.headline,
    ctx.params,
    ctx.requestContext,
    subjectLabelFallback,
    flowType,
    trafficSource,
    pageTenantId,
    siteConfig?.displayName,
    siteConfig?.domain,
    ctx.correlationId,
    comparison?.snapshot,
    submitted?.snapshot,
    decisionsChannel,
    platformVariant,
  ]);

  /**
   * Seed DSM's `formState` from the **current step's** DE
   * `flowState` entry on mount so `_bind.value` / `_bind.disabled`
   * (and any other `_bind.*` that reads
   * `{{formState.<leadField>}}`) resolve against already-known
   * context rather than starting empty.
   *
   * The primary motivation is the
   * `window.HE.contextualDefaults` → DCS auto-population path
   * (`initializeAutoPopulation`): on Psychology.org-style pages
   * the host hands `<DynamicExperienceProvider>` a `dcs` prop with
   * `{ degree, category, subject }` slugs, which lands as an
   * `Option[]` on `flowState[entryStepId]`. Without this seed the
   * DSL Selects start blank even though the user's degree /
   * category / subject are already settled by page content
   * (EDUN-12336).
   *
   * Scope: only the current step's flowState entry. DSM's
   * `formState` represents what is selected **in this step**;
   * `onFormStateChange` (below) translates the full next snapshot
   * into a `flowState[currentStep.id]` write, so cross-step
   * aggregation would silently re-emit prior steps' selections
   * under the current step on the first user interaction. Authors
   * that need a prior step's selection still get it via
   * `flowContext` (`{{context.<leadField>}}`) — a separate
   * interpolation channel that does not feed `updateFlowState`.
   *
   * Memoized on `[flowState, currentStep.id]`; the DSM provider
   * applies the result once via the lazy reducer initializer, so
   * later updates flow through the standard `updateFormState`
   * channel (not a reseed).
   */
  const landingHostSeed = useMemo(() => {
    const seed = deriveInitialFormState(
      ctx.flowState,
      ctx.currentStep ? [ctx.currentStep] : [],
    );
    const stepId = ctx.currentStep?.id;
    return resolveLandingHostFormSeed({
      seed,
      hostInitialFormState: ctx.hostInitialFormState,
      currentStepId: stepId,
      entryStepId: ctx.entryStepId,
      jumpToStepId: ctx.jumpToStepId,
      steps: ctx.steps,
      flowState: ctx.flowState,
      schoolId: ctx.schoolId,
      ownedAppliedHostKeys: stepId
        ? ctx.appliedHostFormKeysByStep?.[stepId]
        : undefined,
    });
  }, [
    ctx.flowState,
    ctx.currentStep,
    ctx.schoolId,
    ctx.hostInitialFormState,
    ctx.appliedHostFormKeysByStep,
    ctx.entryStepId,
    ctx.jumpToStepId,
    ctx.steps,
  ]);
  const initialFormState = landingHostSeed.formState;
  const hostSeededFormKeys = landingHostSeed.appliedHostKeys;
  const hostSeededFormKeysRef = useRef(hostSeededFormKeys);
  hostSeededFormKeysRef.current = hostSeededFormKeys;

  /**
   * Pre-built `formKey → BoundComponentInfo` index. Walking the DSL
   * `data.content` tree once per build keeps form-state translation at
   * amortized O(1) per changed key (plan §3.10 performance budget).
   */
  const boundIndex = useMemo<Record<string, BoundComponentInfo>>(
    () =>
      buildBoundComponentIndex(
        (dslConfig?.data as { content?: unknown } | undefined)?.content ?? [],
      ),
    [dslConfig?.data],
  );
  const boundFieldIndex = useMemo(
    () =>
      buildBoundFieldIndex(
        (dslConfig?.data as { content?: unknown } | undefined)?.content ?? [],
      ),
    [dslConfig?.data],
  );

  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const boundIndexRef = useRef(boundIndex);
  boundIndexRef.current = boundIndex;
  const boundFieldIndexRef = useRef(boundFieldIndex);
  boundFieldIndexRef.current = boundFieldIndex;
  const dataContentRef = useRef<unknown>(undefined);
  dataContentRef.current = (
    dslConfig?.data as { content?: unknown } | undefined
  )?.content;

  /**
   * Mirror of the latest DSM `sources` snapshot. Populated by the
   * `<SourcesObserver/>` child; read from `onFormStateChange` so display
   * labels resolve against the most recent fetch results.
   */
  const sourcesRef = useRef<Readonly<Record<string, ResolvedDataSource>>>({});

  /**
   * Mirror of the latest DSM `updateFormState` callback. Same
   * pattern as `sourcesRef` — populated by `<SourcesObserver/>`
   * (which lives inside the DSM provider) and read by the action
   * dispatcher (which is constructed outside it). Lets the
   * `formState.clear` action handler reach `dsm.updateFormState`
   * without colocating the dispatcher under the provider.
   *
   * Initial value `null`: dispatcher reads `.current?.()` so a
   * pre-mount call (e.g. during the first render of a child that
   * fires `onSourceChange` synchronously — not a real path today
   * but defensible posture) silently no-ops instead of crashing
   * on a missing function.
   */
  const updateFormStateRef = useRef<
    ((key: string, value: FormStateValue) => void) | null
  >(null);
  const updateFormStateManyRef = useRef<
    ((entries: Readonly<Record<string, string>>) => void) | null
  >(null);
  const getFormStateRef = useRef<(() => FormState) | null>(null);

  const prevFormStateRef = useRef<FormState>({});
  const emitObservedFormStateChange =
    useEmitObservedFormStateChange(initialFormState);

  /**
   * Bridge the async IP-inferred country code into the DSM `formState`.
   * `DynamicExperienceProvider` resolves location after mount and writes
   * it to the global `flowState.location` slot. The one-time DSM seed
   * (`deriveInitialFormState`) only catches it when it is already present
   * at mount; when it lands later we push it through `updateFormState` so
   * any source templated on `{{formState.countryCode}}` (e.g. the
   * offerings budget `geolocation`) re-fires. `updateFormState` no-ops on
   * an unchanged value, so running this on every flowState change is safe.
   */
  const inferredCountryCode = readInferredCountryCode(ctx.flowState);
  useEffect(() => {
    if (inferredCountryCode) {
      updateFormStateRef.current?.('countryCode', inferredCountryCode);
    }
  }, [inferredCountryCode]);

  /**
   * Resolve the host-supplied legacy school id(s) to organization UUIDs and
   * seed `formState.organizationIds` so offerings sources templated on
   * `{{formState.organizationIds}}` fire with UUIDs — offerings is UUID-only,
   * so the experience layer owns the legacy-id → org-id resolution (cached in
   * `resolveOrganizationIds`). The conditional-execution guard keeps those
   * sources `pending` until the value lands, then re-fires them, so there is
   * no empty-filter request in flight.
   */
  const orgServiceBaseUrl =
    apiConfig.organizationServiceBaseUrl ?? apiConfig.organizationApiUrl;
  const schoolId = ctx.schoolId;
  useEffect(() => {
    if (!schoolId || !orgServiceBaseUrl) return;
    const edudirectIds = schoolId
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value));
    if (!edudirectIds.length) return;

    let cancelled = false;
    void resolveOrganizationIds(edudirectIds, orgServiceBaseUrl).then(
      (organizationIds) => {
        if (cancelled || !organizationIds.length) return;
        updateFormStateRef.current?.(
          'organizationIds',
          organizationIds.join(','),
        );
      },
    );
    return () => {
      cancelled = true;
    };
  }, [schoolId, orgServiceBaseUrl]);

  /**
   * Bridge the resolved geolocation (ISO 3166-2, e.g. "US-VA") into the DSM
   * `formState`. The `flowState.location` slot is updated both when the IP
   * inference resolves after mount and when a location question (ZIP) is
   * validated on step-forward (`useHandleStepForward` writes the resolved
   * `stateAbbr` into `location.state`). Either change must re-fire any
   * source templated on `{{formState.geolocation}}` (the offerings
   * `geolocation`). `updateFormState` no-ops on an unchanged value.
   */
  const resolvedGeolocation = resolveGeolocation(ctx.flowState);
  useEffect(() => {
    if (resolvedGeolocation) {
      updateFormStateRef.current?.('geolocation', resolvedGeolocation);
    }
  }, [resolvedGeolocation]);

  /**
   * Mirror of the latest `ComparisonProvider` context. Populated
   * only when an ancestor mounted a `ComparisonProvider`; otherwise
   * stays `null` and we register the `comparison.toggle` warn-stub
   * in the dispatcher (matches `createActionDispatcher`'s
   * "registered unconditionally" comment around the dep). Read
   * through `comparisonRef.current` so the dispatcher (built once
   * at mount) always sees the latest `togglePrograms` callback even
   * though selection state changes cause re-renders here. We
   * deliberately read `togglePrograms` lazily on every dispatch so
   * a host that swaps providers (e.g. via a key change) is honored
   * without rebuilding the dispatcher.
   */
  const comparisonRef = useRef(comparison);
  comparisonRef.current = comparison;

  /**
   * Mirror of the governed lead-flow entry point. Present only when an
   * ancestor mounted a
   * `LeadFlowProvider` (result configs do, via the outer
   * `ConfigurableStep`). Read lazily through `leadFlowRef.current` on
   * every dispatch so a late mount lights up `results.openLeadFlow` on
   * the next dispatch without rebuilding the dispatcher.
   */
  const leadFlow = useLeadFlowOptional();
  const leadFlowRef = useRef(leadFlow);
  leadFlowRef.current = leadFlow;

  const modalHost =
    typeof useModalHostOptional === 'function' ? useModalHostOptional() : null;
  const modalHostRef = useRef(modalHost);
  modalHostRef.current = modalHost;
  // Outlives dispatcher rebuilds, so an unchanged repeat after success stays ignored.
  const [leadSubmitSubmissions] = React.useState(
    () => new Map<string, string>(),
  );

  /**
   * `apiConfig.externalLinkOrigins` -- per child SAD §11 and the
   * DB-2.1 planning decision, this is a separate allowlist from
   * DSM's `trustedOrigins`. Defaults to `[]` (deny-all) per env in
   * `ENDPOINT_MAP`; populating a domain is a one-line PR per env
   * once a DSL author actually needs an external link.
   */
  const externalLinkOrigins = useMemo(
    () => apiConfig.externalLinkOrigins ?? [],
    [apiConfig.externalLinkOrigins],
  );

  /**
   * `navigation.linkout` host context. The action is registered
   * only when the tenant slug is known, mirroring the `externalLinkOrigins`
   * opt-in posture. `baseUrl` defaults to same-origin (`leadApiBaseUrl ?? ''`)
   * so the browser only ever talks to a relative BFF route. Returns `undefined`
   * when no tenant is configured so the dispatcher leaves the action
   * unregistered.
   */
  const leadApiBaseUrl = apiConfig.leadApiBaseUrl;
  /**
   * Tenant tagular apiKey (a UUID) for the CMS v2 `trackingContext.tenantId`.
   * Distinct from the tenant slug (`pageTenantId`): CMS validates this field as
   * `@IsUUID()`, so we forward the validated apiKey or omit it (same resolution
   * the Voyager POST path uses via `buildConversionCreationRequest`).
   */
  const trackingTenantId = useMemo(
    () => (siteConfig ? resolveTrackingTenantId(siteConfig) : undefined),
    [siteConfig],
  );
  const linkoutRedirect = useMemo(
    () =>
      pageTenantId
        ? {
            tenantId: pageTenantId,
            baseUrl: leadApiBaseUrl,
            flowType,
            trafficSource,
            monetizationChannel,
            /**
             * Restore Mobius parity: source the live Cohesion identity +
             * monetization classification at click time so the Linkout / HLF
             * GET redirect carries the same `trackingContext` the Voyager POST
             * does. `formatType` / `formatSubtype` ride through from the action
             * params (the DSL card binds them from its resolved eventing);
             * `buildLinkoutTrackingContext` supplies the `dexp-offering`
             * default and reads Cohesion for the identity fields.
             */
            getTrackingContext: (
              params?: Readonly<Record<string, unknown>>,
            ): Record<string, unknown> | undefined =>
              buildLinkoutTrackingContext({
                trackingTenantId,
                formatType:
                  typeof params?.formatType === 'string'
                    ? params.formatType
                    : undefined,
                formatSubtype:
                  typeof params?.formatSubtype === 'string'
                    ? params.formatSubtype
                    : undefined,
              }),
          }
        : undefined,
    [
      pageTenantId,
      leadApiBaseUrl,
      flowType,
      trafficSource,
      monetizationChannel,
      trackingTenantId,
    ],
  );

  /**
   * Handler closures read the latest DE context via `ctxRef.current`.
   * The dispatcher still rebuilds when a host capability in the
   * dependency list appears or disappears. Pending `stub.simulate`
   * timers are cancelled in the effect below so a rebuild or unmount
   * cannot run their follow-ups.
   *
   * AC 5 (provider-stack extensibility) is a *design* property --
   * adding S9 providers (Tracking / Eventing / Comparison) is a
   * one-line edit at the call site or wrapper, not in this file.
   * The new {@link createActionDispatcher} factory's `extraHandlers`
   * + `onAfterDispatch` seams (DB-2.1) make that explicit.
   */
  const dispatcher = useMemo(
    () =>
      createActionDispatcher({
        advanceStep: () => ctxRef.current.handleStepForward?.(),
        advanceStepWithoutSubmit: () =>
          ctxRef.current.handleStepForward?.({ refuseSubmission: true }),
        // SAD `navigation.restartFlow` -- always wired (every DE
        // context now exposes `handleRestart` after DB-2.1).
        restartFlow: () => ctxRef.current.handleRestart?.(),
        // SAD `navigation.externalLink`. The default `window.open`
        // thunk inside the factory is sufficient; we only need to
        // hand in the origin allowlist (deny-all per env until a
        // DSL author whitelists a domain).
        externalLinkOrigins,
        // `navigation.linkout` -- wired only when the tenant slug
        // is known. The factory builds the same-origin BFF redirect URL and
        // opens it synchronously; the BFF creates the conversion server-side
        // and 302s to the CMS-interpolated linkout.
        linkoutRedirect,
        // SAD `submission.requestInfo` -- delegate to the host's
        // `useHandleSubmit` callback the Question step already uses.
        // We forward an explicit `result?` step override when the
        // DSL supplies one (no shape today; DB-3.A will tighten).
        submitRequestInfo: async () => {
          await ctxRef.current.handleStepForward?.();
        },
        // `results.openLeadFlow` -- wired only when a `LeadFlowProvider`
        // is mounted in scope (result configs). The DSL hands off the
        // selected row verbatim via `params.result: '{{item}}'`, so we
        // validate it with `isSubmittableResult` (numeric `programId` plus
        // forward `offeringUuid` or the legacy school quartet, not just a
        // `programId`) before delegating to the governed drawer. `leadFlowRef.current`
        // is read lazily so a late provider mount lights up the action on
        // the next dispatch without rebuilding the dispatcher.
        openLeadFlow: leadFlowRef.current
          ? (params) => {
              const fn = leadFlowRef.current?.openLeadFlow;
              if (!fn) return;
              const result = (params as { result?: unknown } | undefined)
                ?.result;
              if (!isSubmittableResult(result)) {
                // eslint-disable-next-line no-console
                console.warn(
                  '[ConfigurableStep] results.openLeadFlow ignored — params.result must be a Result with a numeric programId and either a non-empty offeringUuid or the full schoolId, schoolSlug, schoolName, and programName quartet.',
                  params,
                );
                return;
              }
              // Also write the selected offering id into DSM formState so the
              // lazy `offeringDetail` by-id source — templated on
              // `{{formState.selectedOfferingId}}` and held `pending` by the
              // conditional-execution guard until a selection exists — fires
              // for this result. The id rides alongside the result via
              // `params.offeringId: '{{item.offeringUuid}}'`. Written before
              // opening the drawer so the detail fetch is in flight as the
              // drawer mounts. A missing/blank id is dropped silently: the
              // drawer still opens and degrades gracefully without the detail
              // source.
              const offeringId = (
                params as { offeringId?: unknown } | undefined
              )?.offeringId;
              if (
                typeof offeringId === 'string' &&
                offeringId.trim().length > 0
              ) {
                updateFormStateRef.current?.('selectedOfferingId', offeringId);
              }
              fn(result);
            }
          : undefined,
        openNamedModal: modalHostRef.current
          ? (params) => {
              const host = modalHostRef.current;
              if (!host) return;
              dispatchNamedModalAction(
                host,
                'modal.open',
                params,
                'ConfigurableStep',
              );
            }
          : undefined,
        closeNamedModal: modalHostRef.current
          ? (params) => {
              const host = modalHostRef.current;
              if (!host) return;
              dispatchNamedModalAction(
                host,
                'modal.close',
                params,
                'ConfigurableStep',
              );
            }
          : undefined,
        // SAD `submission.apiCall` -- thin httpClient that defers to
        // `fetchDataSource` so the same trustedOrigins +
        // coerceRequestBody policies that DSM enforces apply to
        // ad-hoc author-driven calls. Uses the action's own
        // request as the declaration; an empty declaration would
        // also satisfy `fetchDataSource` but reusing the request
        // shape keeps the call site minimal.
        httpClient: async (req) => {
          const decl = {
            endpoint: req.endpoint,
            method: req.method,
            ...(req.headers ? { headers: req.headers } : {}),
            ...(req.body !== undefined ? { body: req.body } : {}),
          } satisfies import('@de/providers/DataSourceManager.types').HttpDataSourceDeclaration;
          // Empty interpolation context: `params` for an action are
          // already pre-interpolated by `resolveActions` against the
          // DSL roots, so a second pass is unnecessary.
          const result = await fetchDataSource(
            decl,
            {},
            {
              trustedOrigins,
              tenantId: pageTenantId,
              coerceRequestBody,
              coerceResponse,
            },
          );
          if (result.status === 'error') {
            throw result.error ?? new Error('submission.apiCall failed');
          }
          return result.data;
        },
        // SAD `comparison.toggle`. Wired only when a
        // `ComparisonProvider` is mounted in scope; otherwise we
        // leave the dep undefined so the factory keeps its
        // unconditional warn-stub registration -- DSL authors who
        // dispatch the type without a provider still get the
        // actionable breadcrumb instead of a silent miss.
        //
        // Contract: DSL emits
        //   _action.<event>: {
        //     type: 'comparison.toggle',
        //     params: { item: '{{item}}' }
        //   }
        // We accept `params.item` as the raw `Result`-shaped object
        // resolved by `_action.params` interpolation. The param
        // name is intentionally generic (`item`, not `program`) to
        // match the admin catalog shape (edudirect-admin
        // `action-catalog.ts` -- `comparison.toggle` ->
        // `params.item: { kind: 'string' }` template that resolves
        // via single-expression interpolation to the bound item
        // object verbatim) so a future non-Programs
        // ComparisonProvider can reuse the same dispatch surface.
        // The handler reads through `comparisonRef.current` so a
        // late mount of the provider lights up real toggling on the
        // *next* dispatch without remounting this step.
        toggleComparison: comparisonRef.current
          ? (params) => {
              const ctx = comparisonRef.current;
              const fn = ctx?.togglePrograms;
              if (!ctx || !fn) return;
              const item = (params as { item?: unknown } | undefined)?.item;
              if (!isResultLike(item)) {
                // eslint-disable-next-line no-console
                console.warn(
                  '[ConfigurableStep] comparison.toggle ignored — params.item must be a Result with a numeric programId.',
                  params,
                );
                return;
              }
              // Emit the same compare analytics the checkbox path fires
              // (`useComparisonForProgram`) so the action-dispatch path is
              // at parity. Derive add vs remove from the resulting
              // selection state: `togglePrograms`
              // returns the post-toggle checked state, so pairing it with
              // the pre-toggle state distinguishes a real remove from an
              // add blocked at the max cap (which must NOT emit, mirroring
              // the checkbox guards). `dslConfigId` rides through from the
              // provider context so the beam carries it on the DSL path.
              const wasSelected = ctx.isProgramSelected(item.programId);
              const nowSelected = fn(item);
              const dslConfigId = ctx.dslConfigId;
              const analytics = {
                schoolSlug: item.schoolSlug,
                viewCorrelationId: item.viewCorrelationId,
                correlationId: item.productClickedCorrelationId,
              };
              if (nowSelected && !wasSelected) {
                emitCompareAdded(analytics, dslConfigId);
              } else if (!nowSelected && wasSelected) {
                emitCompareRemoved(analytics, dslConfigId);
              }
            }
          : undefined,
        goToStep: ctxRef.current.handleGoToStep
          ? (stepId, opts) => ctxRef.current.handleGoToStep?.(stepId, opts)
          : undefined,
        goBack: ctxRef.current.handlePrevious
          ? () => ctxRef.current.handlePrevious?.()
          : undefined,
        // `formState.clear` is registered unconditionally (not gated
        // on a host capability the way `goToStep` / `goBack` are) --
        // every host has a DSM, so every host can clear formState
        // slots. The handler reads through `updateFormStateRef.current`
        // so it always sees the latest DSM callback even though the
        // dispatcher itself is built once at mount.
        clearFormState: (keys) => {
          const fn = updateFormStateRef.current;
          if (!fn) return;
          for (let i = 0; i < keys.length; i++) {
            fn(keys[i], '');
          }
        },
        // Same ref pattern as `clearFormState`. Prefer the batched
        // writer so one source that reads several patched keys
        // refetches once. Fall back to per-key writes when a host
        // DSM predates `updateFormStateMany`.
        setFormState: (entries) => {
          const many = updateFormStateManyRef.current;
          if (many) {
            many(entries);
            return;
          }
          const fn = updateFormStateRef.current;
          if (!fn) return;
          for (const [key, value] of Object.entries(entries)) {
            fn(key, value);
          }
        },
        readFormState: () => getFormStateRef.current?.() ?? {},
        leadSubmit: {
          getFieldIndex: () => boundFieldIndexRef.current,
          getContextParams: () => ctxRef.current.params,
          getTrackingContext: () =>
            buildTrackingContext(ctxRef.current.correlationId),
          onFormSubmitted: (event) =>
            emitLeadFormSubmitted(ctxRef.current.eventContext, event),
          baseUrl: leadApiBaseUrl,
          dryRun,
          submissions: leadSubmitSubmissions,
        },
      }),
    // `externalLinkOrigins` and `trustedOrigins` are derived from
    // `apiConfig`, which is stable in practice (host configures it
    // once at mount). Including them in deps for correctness; the
    // dispatcher will rebuild if the host ever swaps configs.
    //
    // `Boolean(comparison)` is included so that mounting / unmounting
    // a `ComparisonProvider` ancestor at runtime flips the dispatcher
    // between the wired handler and the factory's warn-stub.
    // Identity-only check (not the full context) so a selection
    // change does not trigger a rebuild — the handler reads
    // through `comparisonRef.current` for that.
    [
      externalLinkOrigins,
      linkoutRedirect,
      trustedOrigins,
      coerceRequestBody,
      coerceResponse,
      pageTenantId,
      Boolean(comparison),
      // Mount / unmount of a `LeadFlowProvider` ancestor flips
      // `results.openLeadFlow` between the wired handler and unregistered.
      // Identity-only check so a selected-result
      // change does not rebuild the dispatcher — the handler reads through
      // `leadFlowRef.current`.
      Boolean(leadFlow),
      Boolean(modalHost),
      leadApiBaseUrl,
      dryRun,
      leadSubmitSubmissions,
    ],
  );

  useEffect(() => {
    return () => {
      dispatcher.dispose?.();
    };
  }, [dispatcher]);

  useEffect(() => {
    const host = modalHostRef.current;
    if (!host) return undefined;
    return host.subscribeClosed(() => {
      dispatcher.cancelPendingSimulations?.();
    });
  }, [dispatcher, modalHost]);

  const onFormStateChange = useCallback(
    (next: FormState) => {
      const prev = prevFormStateRef.current;
      const stepId = ctxRef.current.currentStep?.id;
      if (!stepId) {
        // Still advance the ref so the next change diffs against the
        // current DSM snapshot — same invariant as the success path.
        prevFormStateRef.current = next;
        return;
      }

      try {
        translateFormStateChange({
          stepId,
          prev,
          next,
          resolveBound: (formKey) =>
            boundIndexRef.current[formKey] ??
            findBoundComponent(dataContentRef.current, formKey),
          resolveSource: (name) => sourcesRef.current[name],
          translateDcsValue: taxonomyRef.current.translateDcsValue,
          unboundHostKeys: hostSeededFormKeysRef.current,
          formByKey: boundFieldIndexRef.current.formByKey,
          commit: (sid, data) => ctxRef.current.updateFlowState?.(sid, data),
        });
      } finally {
        // Update only after `translate` returns so a thrown commit
        // does not leave the ref ahead of the last successfully
        // diffed state (review N4). `translate` swallows resolver
        // errors per-key today; the `finally` is defense-in-depth.
        prevFormStateRef.current = next;
        emitObservedFormStateChange(stepId, next);
      }
    },
    [emitObservedFormStateChange],
  );

  const renderedDslConfig = isResultDslConfig ? (
    <ResultPageLoadingGate>
      <DslSubmissions />
      <DslConfig
        dslConfig={dslConfig}
        excludedContentTypes={RESULT_PAGE_EXCLUDED_CONTENT_TYPES}
        renderErrorFallback={renderErrorFallback}
        onRenderError={onRenderError}
      />
      <DslResultsPagePopups />
    </ResultPageLoadingGate>
  ) : (
    <DslConfig
      dslConfig={dslConfig}
      renderErrorFallback={renderErrorFallback}
      onRenderError={onRenderError}
    />
  );

  return (
    <HostSeededFormKeysProvider keys={hostSeededFormKeys}>
      <DataSourceManagerProvider
        dataSources={dataSources}
        flowContext={flowContext}
        initialFormState={initialFormState}
        endpointExcludedFormKeys={hostSeededFormKeys}
        onFormStateChange={onFormStateChange}
        trustedOrigins={trustedOrigins}
        tenantId={pageTenantId}
        reportingQueryUrl={reportingQueryUrl}
        coerceRequestBody={coerceRequestBody}
        coerceResponse={coerceResponse}
      >
        <SourcesObserver
          sourcesRef={sourcesRef}
          updateFormStateRef={updateFormStateRef}
          updateFormStateManyRef={updateFormStateManyRef}
          getFormStateRef={getFormStateRef}
        />
        {/* Result configs lift the `offeringDetail` by-id source up to the
          ancestor `LeadFlowProvider` for the drawer-detail merge. Lives here
          (inside the DSM) because `LeadFlowProvider` is an ancestor of this
          bridge and cannot read the DSM directly. No-ops when no
          `LeadFlowProvider` is mounted (non-result configs). */}
        {isResultDslConfig && <OfferingDetailObserver />}
        <ActionDispatcherContext.Provider value={dispatcher}>
          <EntryRoot>{renderedDslConfig}</EntryRoot>
        </ActionDispatcherContext.Provider>
      </DataSourceManagerProvider>
    </HostSeededFormKeysProvider>
  );
};

export default ConfigurableStep;

/**
 * Subscribes to the DSM context and mirrors the relevant slices
 * into parent-owned refs. Lives inside the
 * `<DataSourceManagerProvider>` so it can call `useDataSourceManager`;
 * the parent (`ConfigurableStep`) does not, because the action
 * dispatcher and `onFormStateChange` callback are constructed
 * outside the provider for stable identity across renders.
 *
 * Two refs are populated:
 *   - `sourcesRef`: latest `sources` snapshot, read by
 *     `onFormStateChange` so display-label resolution sees
 *     post-resolve data without recreating the callback closure.
 *   - `updateFormStateRef`: latest `updateFormState` callback, read
 *     by the `formState.clear` action handler so dispatched clears
 *     reach DSM through the same code path as user-driven changes.
 *   - `updateFormStateManyRef`: latest batched writer, read by
 *     `formState.set` so a multi-key patch refetches once.
 *   - `getFormStateRef`: live formState read, so a cleared pending flag
 *     is visible when a local simulation delay ends.
 */
const SourcesObserver: React.FC<{
  readonly sourcesRef: React.MutableRefObject<
    Readonly<Record<string, ResolvedDataSource>>
  >;
  readonly updateFormStateRef: React.MutableRefObject<
    ((key: string, value: FormStateValue) => void) | null
  >;
  readonly updateFormStateManyRef: React.MutableRefObject<
    ((entries: Readonly<Record<string, string>>) => void) | null
  >;
  readonly getFormStateRef: React.MutableRefObject<(() => FormState) | null>;
}> = ({
  sourcesRef,
  updateFormStateRef,
  updateFormStateManyRef,
  getFormStateRef,
}) => {
  const { sources, updateFormState, updateFormStateMany, getFormState } =
    useDataSourceManager();
  sourcesRef.current = sources;
  getFormStateRef.current = getFormState ?? null;
  updateFormStateRef.current = updateFormState;
  updateFormStateManyRef.current = updateFormStateMany ?? null;
  return null;
};

/**
 * DSL source name the drawer-detail observer watches. The DSL author
 * declares the lazy offering detail-by-id source under this key
 * (`dataSources.offeringDetail`, templated on
 * `{{formState.selectedOfferingId}}`); this bridge lifts its resolved
 * value up to the `LeadFlowProvider` for the non-destructive drawer merge.
 */
const OFFERING_DETAIL_SOURCE_NAME = 'offeringDetail';
export const RESULTS_LOADING_INTERSTITIAL_TIMEOUT_MS = 10_000;

/**
 * Lifts the `offeringDetail` DSM source up to the ancestor `LeadFlowProvider`.
 *
 * `LeadFlowProvider` wraps this bridge, so it is an *ancestor* of the
 * `DataSourceManagerProvider` and cannot call `useDataSourceManager()`
 * itself. This observer lives inside the DSM, reads the named source,
 * and pushes it up through `applyOfferingDetail`. The provider owns the
 * merge/staleness/match policy; this component only forwards state.
 *
 * No-ops when no `LeadFlowProvider` is in scope (`applyOfferingDetail`
 * undefined) or when the author declared no `offeringDetail` source.
 */
const OfferingDetailObserver: React.FC = () => {
  const { sources } = useDataSourceManager();
  const leadFlow = useLeadFlowOptional();
  const applyOfferingDetail = leadFlow?.applyOfferingDetail;
  const offeringDetail = sources[OFFERING_DETAIL_SOURCE_NAME];

  useEffect(() => {
    applyOfferingDetail?.(offeringDetail);
  }, [applyOfferingDetail, offeringDetail]);

  return null;
};

const ResultPageLoadingGate: React.FC<{
  readonly children: React.ReactNode;
}> = ({ children }) => {
  const { getSource } = useDataSourceManager();
  const { shouldShowLoadingInterstitial, getLoadingCopy } =
    useResultsPageConfig();
  const primaryResults = getSource(PRIMARY_RESULTS_SOURCE_NAME);
  const [hasTimedOut, setHasTimedOut] = React.useState(false);
  const isPrimaryResultsPending =
    shouldShowLoadingInterstitial &&
    primaryResults &&
    primaryResults.status === 'pending';

  useEffect(() => {
    if (!isPrimaryResultsPending) {
      setHasTimedOut(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setHasTimedOut(true);
    }, RESULTS_LOADING_INTERSTITIAL_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isPrimaryResultsPending]);

  if (isPrimaryResultsPending && !hasTimedOut) {
    return <Loading {...getLoadingCopy()} />;
  }

  return <>{children}</>;
};

/**
 * Tiny wrapper that wires the DSL subtree's DOM root to
 * `TrackingProvider`'s `setEntryRootNode` ref callback.
 *
 * Sits inside `<TrackingProvider>` because it needs `useTracking()`;
 * keeping the provider DOM-free is intentional (mirrors the
 * eventing-package direction in the Eventing Strategy SAD §6.6).
 *
 * The `data-de-entry-root` attribute is informational only — it
 * helps integration tests and the browser inspector identify the
 * observed node. The firing contract is the ref callback.
 *
 * Layout note: a plain `<div>` (default `display: block`) is used
 * rather than `display: contents` because the hook's
 * `IntersectionObserver` must see a real layout box. The
 * `Trackable.tsx` wrapper uses `display: contents` and observes
 * `firstElementChild` to dodge the layout-neutral-vs-IO trade-off;
 * we observe the wrapper directly here because the entry root sits
 * at the top of the DSL subtree and a single block-level box is
 * layout-safe in practice (entry steps render as full-width
 * panels).
 *
 * When `tracking` is `undefined` (host mounts `ConfigurableStep`
 * without a `TrackingProvider`, e.g. legacy snapshot tests), the
 * wrapper still renders so the subtree's DOM is unchanged; the ref
 * callback is just absent.
 */
const EntryRoot: React.FC<{ readonly children: React.ReactNode }> = ({
  children,
}) => {
  const tracking = useTracking();
  return (
    <div ref={tracking?.setEntryRootNode} data-de-entry-root>
      {children}
    </div>
  );
};
